/**
 * @file        src/components/FlightItinerary.jsx
 * @description Collapsible flight-itinerary card shared by the results fare
 *              modal and the booking page. Collapsed it is the one-line summary
 *              the pages already showed; expanded (chevron) it reveals the REAL
 *              per-segment detail from the supplier trip — departure/arrival
 *              times, full airport name + terminal, airline + flight number,
 *              aircraft type and cabin, next-day arrival, and the layover
 *              between connecting segments.
 *
 *              Segment detail comes from `flight._raw.sI` (the TripJack trip we
 *              already keep on every card). A leg without `_raw` (mock / older
 *              store state) still renders — it degrades to a single segment
 *              built from the card's own from/to/dep/arr fields. Nothing here is
 *              fabricated: a field the supplier does not send is simply omitted.
 *
 *              One component, so one-way / round-trip / multi-city all get the
 *              same card — callers just render one per leg.
 * @author      Kinetika Labs
 * @created     2026-07-12
 * @changelog
 *   - 2026-09-22 Claude — Segment row rebuilt as a 3-COLUMN GRID so the expanded
 *                          detail reads on a phone. The old flex kept a time rail
 *                          mirroring the detail column line for line, which only
 *                          holds while every line is one line tall — so the meta
 *                          line had to `truncate` and duly clipped mid-word on
 *                          mobile ("… · Economy class · 2h 2…"), with no hover
 *                          there to reveal the rest. Each time now shares a ROW
 *                          with its own airport, the connector spans all three,
 *                          and the meta line wraps instead. The layover line's
 *                          indent follows the rail's mobile width. Desktop is
 *                          visually unchanged.
 *   - 2026-09-12 Claude — New `compact` prop: the collapsed header at px-3 py-2,
 *                          a 30px logo, 13/11px type and a 28px chevron. Only
 *                          <FareOptionsModal> passes it — that dialog has to show
 *                          its fare cards and their BOOK NOW buttons without the
 *                          customer scrolling, and this row sits above them. Every
 *                          other caller renders exactly as before.
 *   - 2026-09-02 Claude — totalMin's own fallback (used only when the card has
 *                          no `durMin`, e.g. restored/mock state) no longer
 *                          diffs first-departure→last-arrival end to end —
 *                          same zone-less-timestamp bug as flights.js's
 *                          toFlightCard(): it silently drops the origin/
 *                          destination UTC-offset gap on any route that
 *                          crosses timezones. Now sums each segment's own
 *                          `durMin` (already correct) plus the ground time
 *                          between segments, which is safe to diff naively
 *                          since a layover's two timestamps share one airport.
 *   - 2026-08-25 Claude — Mobile-only: time rail narrowed (w-14 → w-11) and the row
 *                          gap tightened (gap-3 → gap-2) below `sm`, so the detail
 *                          column has more room and stops truncating/wrapping on a
 *                          phone. Desktop (`sm:` and up) is unchanged.
 *   - 2026-08-20 Claude — fromLine/toLine in the expanded segment detail no longer
 *                          `truncate` — a long airport name ("Delhi Indira Gandhi
 *                          Int…") clipped with no way to read the rest on mobile
 *                          (no hover there). They wrap instead, and now carry a
 *                          `title` so desktop still gets a hover tooltip.
 *   - 2026-08-17 Claude — Layover line recoloured amber-400 → the `sky` theme token,
 *                          in step with the results card's layover chip: the fixed
 *                          amber was tuned for the dark panel and washed out on light.
 *                          Both were changed together so the same fact is not two
 *                          different colours between search and checkout.
 *   - 2026-08-03 Claude — Segment row: the time rail now mirrors the detail column's
 *                          three-line rhythm, so the arrival time sits beside the
 *                          ARRIVAL AIRPORT. It was dep + mt-2 + arr — two lines against
 *                          three — which parked the landing time next to the flight-
 *                          number line instead. Dropped `leading-tight` for the same
 *                          reason: 17.5px against the detail column's 20px compounded
 *                          the drift on every row.
 *   - 2026-07-12 Claude — Initial (expandable flight details on results + book).
 */
import { useState } from 'react';
import AirlineLogo from './AirlineLogo.jsx';

// "2026-07-12T22:00" → "22:00"
const timeOf = (iso) => {
  const m = String(iso || '').match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
};

// "2026-07-13T00:25" → "Jul 13"
const dayOf = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const d = new Date(`${m[0]}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

// "2026-07-12T22:00" → "Sun, Jul 12"
const dateLabel = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const d = new Date(`${m[0]}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
};

const minutesBetween = (a, b) => {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Number.isNaN(ms) ? 0 : Math.max(0, Math.round(ms / 60000));
};

const durLabel = (mins) => {
  if (!mins) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h ? `${h}h ` : ''}${m}m`.trim();
};

// Calendar-day difference (not elapsed hours): a 23:50 → 00:25 hop is "+1".
const dayDiff = (a, b) => {
  const d1 = String(a || '').slice(0, 10);
  const d2 = String(b || '').slice(0, 10);
  if (!d1 || !d2) return 0;
  return Math.round((new Date(`${d2}T00:00:00`) - new Date(`${d1}T00:00:00`)) / 86400000);
};

const term = (t) => (t ? ` T${String(t).replace(/\D/g, '') || t}` : '');

// TripJack full-word cabin codes.
const CABIN_LABEL = {
  ECONOMY: 'Economy class',
  PREMIUM_ECONOMY: 'Premium Economy class',
  BUSINESS: 'Business class',
  FIRST: 'First class',
};

/**
 * Normalise a flight card into the segment list the expanded view renders.
 * Prefers the supplier's real segment chain; falls back to one synthetic
 * segment so a leg without `_raw` still expands to something truthful.
 */
function segmentsOf(f) {
  const raw = Array.isArray(f?._raw?.sI) ? f._raw.sI : [];
  if (raw.length) {
    return raw.map((s) => ({
      depISO: s.dt,
      arrISO: s.at,
      fromCode: s.da?.code || '',
      fromName: s.da?.name || '',
      fromCity: s.da?.city || s.da?.cityCode || '',
      fromTerm: s.da?.terminal || '',
      toCode: s.aa?.code || '',
      toName: s.aa?.name || '',
      toCity: s.aa?.city || s.aa?.cityCode || '',
      toTerm: s.aa?.terminal || '',
      airline: s.fD?.aI?.name || s.fD?.aI?.code || '',
      airlineCode: s.fD?.aI?.code || '',
      flightNo: s.fD?.aI?.code && s.fD?.fN ? `${s.fD.aI.code}${s.fD.fN}` : '',
      aircraft: s.fD?.eT || '',           // equipment type — often absent
      durMin: s.duration || minutesBetween(s.dt, s.at),
    }));
  }
  // Fallback: the card's own summary fields (mock legs / restored store state).
  return [{
    depISO: f?.depISO,
    arrISO: f?.arrISO,
    fromCode: f?.fromCode || String(f?.from || '').split(' ')[0],
    fromName: '',
    fromCity: '',
    fromTerm: '',
    toCode: f?.toCode || String(f?.to || '').split(' ')[0],
    toName: '',
    toCity: '',
    toTerm: '',
    airline: f?.airline || '',
    airlineCode: f?.logo || '',
    flightNo: f?.code || '',
    aircraft: '',
    durMin: f?.durMin || minutesBetween(f?.depISO, f?.arrISO),
  }];
}

// One segment: times on the left rail, airport + airline detail on the right.
function SegmentRow({ s, cabin }) {
  const dep = timeOf(s.depISO) || '—';
  const arr = timeOf(s.arrISO) || '—';
  const plus = dayDiff(s.depISO, s.arrISO) > 0;
  const fromLine = [s.fromCode, s.fromName || s.fromCity].filter(Boolean).join(' ') + term(s.fromTerm);
  const toLine = [s.toCode, s.toName || s.toCity].filter(Boolean).join(' ') + term(s.toTerm);
  const meta = [s.airline, s.flightNo, s.aircraft, cabin].filter(Boolean).join(' · ');
  const metaLine = (meta || '—') + (s.durMin ? ` · ${durLabel(s.durMin)}` : '');

  // The row is a 3-COLUMN GRID, not two independently stacked columns.
  //
  // It used to be a flex of a time rail and a detail column, the rail mirroring
  // the detail column's three lines line for line. That only stays in step while
  // EVERY line is exactly one line tall, which is why the meta line had to
  // `truncate` — and on a phone it duly clipped mid-word ("AI Express · IX1026 ·
  // 7M8 · Economy class · 2h 2…") with no hover there to reveal the rest.
  //
  // In the grid each time shares a ROW with the airport it belongs to, so the
  // middle row is free to wrap to two or three lines and the arrival time still
  // lands beside the arrival airport. Nothing needs truncating any more.
  //   - Column 1 is the time rail, narrower on mobile (2.75rem vs 3.5rem) so the
  //     detail column keeps the width it needs on a phone.
  //   - Column 2 is the connector, spanning all three rows so it stretches with a
  //     wrapped meta line instead of leaving the dots adrift.
  //   - Column 3 is `minmax(0,1fr)` — a bare `1fr` has a min-width of `auto`, so a
  //     long unbroken airport name would push the grid wider than the card.
  return (
    <div className="grid grid-cols-[2.75rem_auto_minmax(0,1fr)] sm:grid-cols-[3.5rem_auto_minmax(0,1fr)] gap-x-2 sm:gap-x-3">
      <p className="col-start-1 row-start-1 text-right font-display text-sm font-extrabold">{dep}</p>
      {/* connector */}
      <div className="col-start-2 row-start-1 row-span-3 flex flex-col items-center pt-1.5 pb-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-neon shrink-0" />
        <span className="flex-1 w-px bg-line my-1" />
        <span className="w-1.5 h-1.5 rounded-full border border-neon shrink-0" />
      </div>
      {/* fromLine/toLine wrap rather than truncate for the same reason as the meta
          line — a clipped "Delhi Indira Gandhi Int…" is unreadable on a phone.
          `title` still gives desktop a hover tooltip. */}
      <p className="col-start-3 row-start-1 text-sm font-semibold break-words" title={fromLine || undefined}>{fromLine || '—'}</p>

      {/* Middle row: the +1-day marker sits opposite the meta line. An nbsp keeps
          the row open as a spacer when the flight lands the same day. */}
      <p className={'col-start-1 row-start-2 text-right text-[11px] my-2 ' + (plus ? 'text-neon' : 'invisible')}>
        {plus ? dayOf(s.arrISO) : <>&nbsp;</>}
      </p>
      <p className="col-start-3 row-start-2 text-[11px] text-muted my-2 break-words" title={metaLine}>
        {metaLine}
      </p>

      <p className="col-start-1 row-start-3 text-right font-display text-sm font-extrabold">{arr}</p>
      <p className="col-start-3 row-start-3 text-sm font-semibold break-words" title={toLine || undefined}>{toLine || '—'}</p>
    </div>
  );
}

/**
 * @param {object}  flight       a flight card (results/store shape).
 * @param {boolean} defaultOpen  start expanded (default: collapsed).
 * @param {boolean} compact      tighter collapsed header, for a dialog that has to
 *   fit everything below it on screen (FareOptionsModal). Expanded detail is the same.
 * @param {string}  title        optional label above the summary line.
 */
export default function FlightItinerary({ flight, defaultOpen = false, title = '', className = '', compact = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!flight) return null;

  const segs = segmentsOf(flight);
  const first = segs[0];
  const last = segs[segs.length - 1];
  const stops = segs.length - 1;
  const totalMin = flight.durMin || segs.reduce(
    (sum, s, i) => sum + (s.durMin || 0) + (i < segs.length - 1 ? minutesBetween(s.arrISO, segs[i + 1].depISO) : 0),
    0,
  );
  const cabin = CABIN_LABEL[String(flight.cabinClass || '').toUpperCase()] || flight.cabinName || '';

  const summary = [
    flight.airline,
    flight.code,
    `${timeOf(first?.depISO) || flight.dep} – ${timeOf(last?.arrISO) || flight.arr}${flight.next ? '⁺¹' : ''}`,
    stops ? `${stops} stop${stops > 1 ? 's' : ''}` : (flight.stops || 'Direct'),
  ].filter(Boolean).join(' · ');

  return (
    <div className={`rounded-xl border border-line bg-night/40 ${className}`}>
      {/* Collapsed header — the whole row is the toggle. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`w-full text-left flex items-center cursor-pointer ${compact ? 'px-3 py-2 gap-2.5' : 'p-4 gap-3'}`}
      >
        <AirlineLogo code={flight.logo || first?.airlineCode} color={flight.color} label={flight.airline} size={compact ? 30 : 36} />
        <div className="min-w-0 flex-1">
          {title && <p className="text-[10px] font-bold uppercase tracking-wide text-neon mb-0.5">{title}</p>}
          <p className={`font-semibold truncate ${compact ? 'text-[13px]' : 'text-sm'}`}>{flight.from} → {flight.to}</p>
          <p className={`text-muted truncate ${compact ? 'text-[11px]' : 'text-xs'}`}>
            {summary}
            {dateLabel(first?.depISO) ? ` · ${dateLabel(first.depISO)}` : ''}
            {durLabel(totalMin) ? ` · ${durLabel(totalMin)}` : ''}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full border border-line flex items-center justify-center text-muted transition ${compact ? 'w-7 h-7' : 'w-8 h-8'} ${open ? 'rotate-180 border-neon text-neon' : ''}`}
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="currentColor" d="M7.4 8.6 12 13.2l4.6-4.6L18 10l-6 6-6-6z" /></svg>
        </span>
      </button>

      {/* Expanded — real per-segment detail + layovers. */}
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-line space-y-3">
          {segs.map((s, i) => (
            <div key={i} className="space-y-3">
              <SegmentRow s={s} cabin={cabin} />
              {/* Layover line — `sky`, the theme token (--nh-sky), not the fixed
                  amber-400 it used to be: that was tuned against the dark panel and
                  washed out on the light theme. Kept in step with the results card's
                  layover chip (FlightCard.jsx) so the same fact is not two different
                  colours between search and checkout. */}
              {i < segs.length - 1 && (
                <p className="ml-11 sm:ml-14 pl-2 sm:pl-3 text-[11px] text-sky">
                  Layover at {segs[i].toCode} · {durLabel(minutesBetween(segs[i].arrISO, segs[i + 1].depISO)) || '—'}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
